import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './referral.css';

const Referral = () => {
    const [business, setBusiness] = useState(null);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [receivers, setReceivers] = useState([{ countryCode: '+92', number: '' }]);
    const sceneRef = useRef(null);

    // Get URL parameters
    const getUrlParams = () => {
        const path = window.location.pathname.split('/');
        return {
            phone_number: path[1] || '',
            business_id: path[2] || ''
        };
    };

    const { phone_number, business_id } = getUrlParams();

    useEffect(() => {
        // Validate parameters
        if (!phone_number || !business_id) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Link',
                text: 'Missing required parameters',
                confirmButtonColor: '#d33'
            });
            return;
        }

        fetchBusinessDetails();
        fetchCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBusinessDetails = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/business/${business_id}`);
            setBusiness(res.data);
        } catch (err) {
            const errorMsg = err.response?.data?.error;
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMsg,
                confirmButtonColor: '#d33'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchCountries = async () => {
        try {
            console.log('Fetching countries from:', `${process.env.REACT_APP_BACKEND_URL}/api/countries`);
            
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/countries`);
            
            console.log('Countries API response:', res.data);
            setCountries(res.data);
            
            // Set default country to first available country
            if (res.data.length > 0) {
                console.log('Setting default country to:', res.data[0].country_code);
                setReceivers([{ countryCode: res.data[0].country_code, number: '' }]);
            } else {
                console.log('No countries found in response');
            }
        } catch (err) {
            console.error('Failed to fetch countries:', err);
            console.error('Error details:', err.response?.data);
            
            // Fallback to some default countries
            setCountries([
                { country_code: '+92', country: 'Pakistan' },
                { country_code: '+1', country: 'United States' }
            ]);
        }
    };

    // Function to trim leading zero from phone number
    const trimLeadingZero = (phoneNumber) => {
        if (phoneNumber.startsWith('0')) {
            return phoneNumber.substring(1);
        }
        return phoneNumber;
    };

    // Function to validate and format phone number
    const formatPhoneNumber = (number) => {
        // Remove any non-digit characters
        let cleaned = number.replace(/\D/g, '');
        
        // Trim leading zero if present
        cleaned = trimLeadingZero(cleaned);
        
        return cleaned;
    };

    const addReceiver = () => {
        const defaultCountry = countries.length > 0 ? countries[0].country_code : '+92';
        setReceivers([...receivers, { countryCode: defaultCountry, number: '' }]);
    };

    const removeReceiver = (index) => {
        if (receivers.length > 1) {
            const newReceivers = receivers.filter((_, i) => i !== index);
            setReceivers(newReceivers);
        }
    };

    const updateReceiver = (index, field, value) => {
        const newReceivers = [...receivers];
        
        if (field === 'number') {
            // Format the phone number as user types (remove leading zero)
            value = formatPhoneNumber(value);
        }
        
        newReceivers[index][field] = value;
        setReceivers(newReceivers);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate receivers
        const validReceivers = receivers.filter(rec => rec.number.trim() !== '');
        if (validReceivers.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'No Numbers',
                text: 'Please add at least one receiver phone number',
                confirmButtonColor: business?.primary_color || '#3085d6'
            });
            return;
        }

        setSubmitting(true);

        try {
            // Format phone numbers - trim leading zeros and combine with country code
            const receiverNumbers = validReceivers.map(rec => {
                const formattedNumber = formatPhoneNumber(rec.number);
                return `${rec.countryCode}${formattedNumber}`;
            });

            console.log('📤 Submitting data:', {
                business_id,
                phone_number,
                receivers: receiverNumbers
            });

            // Call the main referral API
            const referralResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/referral`, {
                business_id,
                phone_number: phone_number,
                receivers: receiverNumbers
            });

            console.log('✅ Main API response:', referralResponse.data);

            // Show success message with details about existing referrals
            let successMessage = referralResponse.data.message;
            
            if (referralResponse.data.existing_receivers && referralResponse.data.existing_receivers.length > 0) {
                successMessage += `\n\nAlready referred: ${referralResponse.data.existing_receivers.join(', ')}`;
            }

            // Also call the dummy tracking API
            try {
                const trackingResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/track-referral`, {
                    business_id,
                    phone_number: phone_number,
                    receivers: receiverNumbers
                });
                
                console.log('📊 Tracking API response:', trackingResponse.data);
            } catch (trackingError) {
                console.warn('⚠️ Tracking API failed (non-critical):', trackingError.message);
            }

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                html: successMessage.replace(/\n/g, '<br>'),
                confirmButtonColor: business?.primary_color || '#3085d6',
            });

            // Reset form
            const defaultCountry = countries.length > 0 ? countries[0].country_code : '+92';
            setReceivers([{ countryCode: defaultCountry, number: '' }]);
            
        } catch (err) {
            console.error('❌ Submission error:', err);
            
            let errorMsg = err.response?.data?.error || 'Failed to submit referrals';
            
            // Handle duplicate referrals error
            if (err.response?.data?.existing_receivers) {
                const existingNumbers = err.response.data.existing_receivers.join(', ');
                errorMsg = `These numbers were already referred by you: ${existingNumbers}`;
            }

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMsg,
                confirmButtonColor: '#d33'
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    if (!business) {
        return (
            <div className="error-container">
                <h2>Business Not Found</h2>
                <p>The business you're looking for doesn't exist or is inactive.</p>
            </div>
        );
    }

    const businessColors = {
        primary: business.primary_color || '#6366f1',
        primaryHighlight: business.primary_highlight_color || '#4f46e5',
        secondary: business.secondary_color || '#f59e0b',
        secondaryHighlight: business.secondary_highlight_color || '#d97706'
    };

    return (
        <div className="referral-container" style={{ 
            '--primary': businessColors.primary, 
            '--primary-highlight': businessColors.primaryHighlight, 
            '--secondary': businessColors.secondary, 
            '--secondary-highlight': businessColors.secondaryHighlight 
        }}>
            <div className="scene" ref={sceneRef}>
                {/* Enhanced 3D Background Elements */}
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <div className="floating-shape shape-3"></div>
                <div className="floating-shape shape-4"></div>
                <div className="floating-shape shape-5"></div>
                
                {/* Animated Particles */}
                <div className="particles-container">
                    {[...Array(20)].map((_, i) => (
                        <div key={i} className="particle" style={{
                            animationDelay: `${i * 0.5}s`,
                            left: `${Math.random() * 100}%`
                        }}></div>
                    ))}
                </div>
                
                <div className="referral-card">
                    {/* Enhanced Business Header */}
                    <div className="business-header">
                        {business.business_logo && (
                            <div className="logo-container">
                                <img 
                                    src={`/images/business_logo/${business.business_logo}`}
                                    alt={business.business_name}
                                    className="business-logo"
                                />
                                <div className="logo-glow"></div>
                            </div>
                        )}
                        <h1 className="business-name">{business.business_name}</h1>
                        <p className="referral-title">Refer Friends & Earn Amazing Rewards</p>
                        <div className="title-underline"></div>
                    </div>

                    {/* Enhanced Sender Info */}
                    <div className="sender-info">
                        <div className="sender-icon">📱</div>
                        <p>Your referral number: <strong>{phone_number}</strong></p>
                    </div>

                    {/* Enhanced Referral Form */}
                    <form onSubmit={handleSubmit} className="referral-form">
                        <div className="receivers-container">
                            {/* Small Heading Only */}
                            <div className="section-heading">
                                <span className="title-icon">👥</span>
                                <h4>Add Friends' Numbers</h4>
                            </div>
                            
                            <div className="receivers-list">
                                {receivers.map((receiver, index) => (
                                    <div key={index} className="receiver-input-group">
                                        <div className="input-row">
                                            <div className="select-wrapper">
                                                <select
                                                    value={receiver.countryCode}
                                                    onChange={(e) => updateReceiver(index, 'countryCode', e.target.value)}
                                                    className="country-select"
                                                >
                                                    {countries.map((country) => (
                                                        <option key={country.country_code} value={country.country_code}>
                                                            {country.country_code} ({country.short_name})
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="select-arrow">▼</div>
                                            </div>
                                            <div className="input-wrapper">
                                                <input
                                                    type="tel"
                                                    value={receiver.number}
                                                    onChange={(e) => updateReceiver(index, 'number', e.target.value)}
                                                    placeholder="Enter phone number"
                                                    className="number-input"
                                                    required
                                                    pattern="[1-9][0-9]*"
                                                    title="Please enter phone number without leading zero"
                                                />
                                                <div className="input-focus"></div>
                                            </div>
                                            {/* Show Add button only on the last input row */}
                                            {index === receivers.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={addReceiver}
                                                    className="add-button-inline"
                                                    title="Add another number"
                                                >
                                                    <span className="button-icon">+</span>
                                                    Add
                                                </button>
                                            )}
                                            {/* Show Remove button for all except first input */}
                                            {receivers.length > 1 && index !== receivers.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeReceiver(index)}
                                                    className="remove-button"
                                                    title="Remove this number"
                                                >
                                                    <span className="remove-icon">×</span>
                                                </button>
                                            )}
                                            {/* Show both buttons when there are multiple inputs */}
                                            {receivers.length > 1 && index === receivers.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeReceiver(index)}
                                                    className="remove-button"
                                                    title="Remove this number"
                                                >
                                                    <span className="remove-icon">×</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="submit-button"
                        >
                            {submitting ? (
                                <>
                                    <div className="button-spinner"></div>
                                    Submitting Referrals...
                                </>
                            ) : (
                                <>
                                    <span className="submit-icon">🚀</span>
                                    Submit {receivers.filter(r => r.number.trim()).length} Referral(s)
                                </>
                            )}
                        </button>
                    </form>

                    {/* Footer */}
                    <div className="referral-footer">
                        <p>Share the love and earn rewards together! 💫</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Referral;